/**
 * Acceptation d'une invitation.
 *
 * Écran PUBLIC : l'invité n'a pas encore de compte utilisable, il ne peut donc
 * pas être authentifié pour arriver ici. Il est rendu AVANT le contrôle de
 * session, sans quoi il renverrait vers l'écran de connexion — celui-là même
 * auquel la personne n'a pas encore accès.
 *
 * Deux temps : choisir son mot de passe, puis — si le rôle l'exige — enrôler la
 * double authentification. Le secret n'est affiché qu'une fois.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';

import { ApiError, api, type Invitation } from '../api/client';
import { ChampMotDePasse } from '../composants/ChampMotDePasse';

const LONGUEUR_MINIMALE = 12;

export function InvitationPage() {
  const { token } = useParams<{ token: string }>();

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [accepte, setAccepte] = useState<{
    mfaSecret: string | null;
    mfaQrDataUri: string | null;
  } | null>(null);

  const charger = useCallback(async () => {
    if (!token) return;
    try {
      setInvitation(await api.readInvitation(token));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Invitation illisible.');
    } finally {
      setChargement(false);
    }
  }, [token]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const accepter = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (motDePasse.length < LONGUEUR_MINIMALE) {
      setErreur(`Le mot de passe doit faire au moins ${LONGUEUR_MINIMALE} caractères.`);
      return;
    }
    if (motDePasse !== confirmation) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      const r = await api.acceptInvitation(token, motDePasse);
      setAccepte({ mfaSecret: r.mfaSecret, mfaQrDataUri: r.mfaQrDataUri });
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Acceptation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="ecran-connexion">
      <div className="carte-connexion">
        <div className="marque">
          <strong>Air Indu SaaS</strong>
          <span>Maintenance, interventions et gestion technique</span>
        </div>

        {chargement && <div className="aide">Chargement de l’invitation…</div>}

        {/* Invitation invalide : on ne propose RIEN d'autre, l'invité doit
            revenir vers la personne qui l'a invité. */}
        {!chargement && !invitation && !accepte && (
          <>
            <div className="message erreur">{erreur ?? 'Invitation introuvable.'}</div>
            <p className="aide">
              Demandez à la personne qui vous a invité de vous envoyer un nouveau lien.
            </p>
          </>
        )}

        {accepte ? (
          <Bienvenue mfaSecret={accepte.mfaSecret} mfaQrDataUri={accepte.mfaQrDataUri} />
        ) : (
          invitation && (
            <form onSubmit={(e) => void accepter(e)}>
              <div className="message info">
                Vous êtes invité à rejoindre <strong>{invitation.tenantName}</strong> en tant que{' '}
                <strong>{invitation.roles.join(', ')}</strong>.
              </div>

              {erreur && <div className="message erreur">{erreur}</div>}

              <div className="champ">
                <label>Adresse électronique</label>
                <input type="text" value={invitation.email} disabled />
                <p className="aide">Ce sera votre identifiant de connexion.</p>
              </div>

              <div className="champ">
                <label htmlFor="mdp">Choisissez un mot de passe</label>
                <ChampMotDePasse id="mdp" autoFocus autoComplete="new-password" value={motDePasse} onChange={setMotDePasse} />
                <p className="aide">{LONGUEUR_MINIMALE} caractères au minimum.</p>
              </div>

              <div className="champ">
                <label htmlFor="mdp2">Confirmez le mot de passe</label>
                <ChampMotDePasse id="mdp2" autoComplete="new-password" value={confirmation} onChange={setConfirmation} />
              </div>

              {invitation.mfaObligatoire && (
                <div className="message info">
                  Votre rôle impose la <strong>double authentification</strong>. Un QR code à
                  scanner vous sera affiché juste après — gardez votre téléphone à portée.
                </div>
              )}

              <button className="pleine-largeur" disabled={enCours}>
                {enCours ? 'Création du compte…' : 'Rejoindre l’espace'}
              </button>
            </form>
          )
        )}
      </div>
    </div>
  );
}

/**
 * Compte créé.
 *
 * Le secret MFA n'est montré qu'ICI : il n'est plus jamais lisible ensuite, même
 * par un administrateur. Le dire évite de fermer la page trop vite.
 */
function Bienvenue({
  mfaSecret,
  mfaQrDataUri,
}: {
  mfaSecret: string | null;
  mfaQrDataUri: string | null;
}) {
  return (
    <>
      <div className="message succes">Votre compte est créé.</div>

      {mfaSecret ? (
        <>
          <div className="message info">
            <strong>Enrôlez votre téléphone maintenant</strong> avec Google Authenticator ou
            Microsoft Authenticator. Ce secret ne sera plus jamais affiché : sans lui, vous ne
            pourrez pas vous connecter.
          </div>

          {/* Le QR est produit par le serveur, qui dispose déjà de la
              bibliothèque : le secret ne transite par aucun service tiers.
              Son absence n'empêche pas l'enrôlement — le code reste saisissable
              à la main juste en dessous. */}
          {mfaQrDataUri && (
            <div className="qr-enrolement">
              <img src={mfaQrDataUri} alt="QR code d’enrôlement de la double authentification" />
            </div>
          )}

          <div className="champ">
            <label>{mfaQrDataUri ? 'Ou saisissez ce code à la main' : 'Saisissez ce code à la main'}</label>
            <input type="text" readOnly value={mfaSecret} onFocus={(e) => e.currentTarget.select()} />
          </div>
        </>
      ) : (
        <p className="aide">Votre rôle ne demande pas de double authentification.</p>
      )}

      <a className="bouton-lien pleine-largeur-lien" href="/">
        Aller à la connexion
      </a>
    </>
  );
}

